import type { ApiMaintenanceEvent } from '@/api/equipmentApi'
import { matchesBoardStatus, type BoardStatus } from '@/lib/board/boardCardView'
import { ASSIGNED, NOT_ASSIGNED, type BoardFilters } from '@/lib/board/boardFilters'

/**
 * Cleaning tasks on the Overall Status board (z8nrz7f9u5).
 *
 * The board lists procedure runs, and a cleaning task only gets a run once
 * someone opens it from Pending Actions. A missed clean usually never was, so
 * the board shows cleaning tasks as cards of their own — the way Tasks merges
 * in process bookings. A task whose run exists is shown by that run's card.
 *
 * Expired: cleaning is daily, so a clean not done on its day is expired from
 * the next day. Calculated, never set by hand.
 */

/** One cleaning task card on the board. */
export interface BoardTask {
  event: ApiMaintenanceEvent
  /** `YYYY-MM-DD`. */
  dueDate: string
  status: BoardStatus
}

/** Where a cleaning task stands on the board, on `today` (`YYYY-MM-DD`). */
export function taskBoardStatus(event: Pick<ApiMaintenanceEvent, 'status' | 'dueDate'>, today: string): BoardStatus {
  if (event.status === 'COMPLETED') return 'complete'
  if (event.dueDate.slice(0, 10) < today) return 'expired'
  return event.status === 'STARTED' ? 'in_progress' : 'not_started'
}

/** The runs of cleaning tasks whose day has passed undone: their cards read Expired too. */
export function expiredRunIds(events: readonly ApiMaintenanceEvent[], today: string): Set<string> {
  return new Set(
    events.flatMap(e => (e.procedureId && taskBoardStatus(e, today) === 'expired' ? [e.procedureId] : [])),
  )
}

/**
 * Whether the board's filters can show cleaning tasks at all: a task has no
 * patient and no customer, so either filter leaves none.
 */
export function boardShowsCleaningTasks(filters: BoardFilters): boolean {
  return (
    (filters.types.length === 0 || filters.types.includes('Cleaning')) && !filters.patientMrn && !filters.customerId
  )
}

/** The task cards these filters keep, run-less tasks only. */
export function buildBoardTasks(
  events: readonly ApiMaintenanceEvent[],
  filters: BoardFilters,
  today: string,
): BoardTask[] {
  if (!boardShowsCleaningTasks(filters)) return []
  const q = filters.q.trim().toLowerCase()
  const seen = new Set<string>()
  return events.flatMap(event => {
    // Fetched by more than one query; and a task with a run is that run's card.
    if (seen.has(event.id) || event.procedureId) return []
    seen.add(event.id)
    const dueDate = event.dueDate.slice(0, 10)
    const status = taskBoardStatus(event, today)
    if (!matchesBoardStatus(status, filters.status)) return []
    if (filters.assigneeId === ASSIGNED ? !event.assigneeId : filters.assigneeId === NOT_ASSIGNED ? !!event.assigneeId : filters.assigneeId && event.assigneeId !== filters.assigneeId) {
      return []
    }
    const created = event.createdAt?.slice(0, 10) ?? ''
    if (filters.createdFrom && created < filters.createdFrom) return []
    if (filters.createdTo && created > filters.createdTo) return []
    if (q) {
      const haystack = [
        event.equipment?.label,
        event.equipmentType?.name,
        event.eventTypeCode,
        event.assignee?.fullName,
        event.assignee?.username,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      if (!haystack.includes(q)) return []
    }
    return [{ event, dueDate, status }]
  })
}
